import {
  Box,
  Card,
  Group,
  MantineStyleProp,
  Text,
  ThemeIcon,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { IconTrophy } from "@tabler/icons-react";
import { useTranslation } from "next-i18next";
import {
  Elimination,
  EliminationMatchup,
  Event,
} from "../../common/events/event.schema";
import { Swords } from "../../common/icons/swords";
import { RoundTitle } from "./EliminationAccordion";
import MatchupDisplay from "./MatchupDisplay";
function getMaxRound(event: Event) {
  let r = 1;
  event.eliminations.map((e) => {
    if (e.complete) r++;
  });
  return r;
}
interface EliminationRound {
  size: number;
  playoff: boolean;
  seeded: boolean;
  elimination: Elimination;
  matchups: EliminationMatchup[];
}
const EliminationBracket = ({
  event,
  openMatch,
  ...others
}: {
  event: Event;
  openMatch: (matchup: EliminationMatchup) => void;
}) => {
  let pW = 1 / event.eliminations.length;
  if (pW < 0.25) pW = 0.25;
  const percentWidth = `${Math.round(100 / pW)}%`;
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme();
  const { t } = useTranslation();
  const maxRound = getMaxRound(event);
  const eliminationRounds: EliminationRound[] = event.eliminations.map(
    (elimination) => {
      // console.log("elimination :", elimination);
      let matchups: EliminationMatchup[] = elimination.matchups;
      if (!elimination.seeds.length) {
        matchups = [];
        for (let index = 0; index < elimination.size / 2; index++) {
          matchups.push({} as EliminationMatchup);
        }
      }
      return {
        matchups,
        size: elimination.size,
        playoff: false,
        seeded: elimination.seeds.length > 0,
        elimination,
      };
    }
  );
  if (event.playoff && eliminationRounds.length > 1) {
    const last = eliminationRounds.pop() as EliminationRound;
    const matchupThird = (last?.matchups[0] || {}) as EliminationMatchup;
    const matchupFinal = (last?.matchups[1] || {}) as EliminationMatchup;
    matchupThird.playoff = true;
    matchupFinal.playoff = false;
    eliminationRounds.push({
      size: 2,
      matchups: [matchupThird],
      playoff: true,
      seeded: !!last?.seeded,
      elimination: last?.elimination,
    });
    eliminationRounds.push({
      size: 2,
      matchups: [matchupFinal],
      playoff: false,
      seeded: !!last?.seeded,
      elimination: last?.elimination,
    });
  }
  const lineColor =
    colorScheme === "dark" ? theme.colors.gray[8] : theme.colors.gray[4];
  return (
    <Box className="event-container" style={{ overflow: "auto" }} {...others}>
      <Box display="flex" className="event-brackets">
        {eliminationRounds.map((eliminationRound, roundIndex) => {
          const matchups = eliminationRound.matchups;
          const lastRound = roundIndex === eliminationRounds.length - 1;
          const firstRound = roundIndex === 0;
          const playoffRound = eliminationRound.playoff;
          const enabled = eliminationRound.seeded;
          //   console.log("matchups :", matchups);
          return (
            <Box
              component="div"
              key={eliminationRound.size}
              className="bracket bracket-1"
              style={{
                width: percentWidth,
                paddingLeft: 0,
                display: "flex",
                margin: 0,
                padding: "10px 0",
                flexGrow: 1,
                flexDirection: "column",
                justifyContent: "space-around",
                listStyleType: "none",
                borderRight: lastRound ? "none" : `1px dashed ${lineColor}`,
                flex: 1,
              }}
            >
              <Group justify="center" ml={10} mb={10}>
                {!eliminationRound.playoff && (
                  <Text size="sm" ta="center">
                    <RoundTitle
                      elimination={eliminationRound.elimination}
                      event={event}
                    />
                  </Text>
                )}
                {eliminationRound.playoff && `${t("Playoff for third")}`}
                {maxRound > eliminationRound.elimination.round && (
                  <ThemeIcon variant="light" size="xs" color="green">
                    <IconTrophy />
                  </ThemeIcon>
                )}
                {maxRound === eliminationRound.elimination.round && (
                  <ThemeIcon variant="light" size="xs">
                    <Swords />
                  </ThemeIcon>
                )}
              </Group>
              <Box
                component="ul"
                style={{
                  paddingLeft: 0,
                  margin: 0,
                  padding: "10px 0",
                  display: "flex",
                  flexGrow: 1,
                  flexDirection: "column",
                  justifyContent: "space-around",
                  listStyleType: "none",
                  flex: 1,
                }}
              >
                {matchups.map((matchup, i) => {
                  let beforeStyle: MantineStyleProp = {
                    content: '""',
                    borderTop: `4px solid ${lineColor}`,
                    position: "absolute",
                    height: 2,
                    width: 10,
                    left: -10,
                    top: "50%",
                  };
                  if (firstRound) beforeStyle = {};
                  // if (lastRound) beforeStyle = {};
                  let afterStyle: MantineStyleProp = {
                    content: '""',
                    borderColor: lineColor,
                    borderWidth: 4,
                    position: "absolute",
                    display: "block",
                    width: 10,
                    right: -11,
                  };
                  if (lastRound) afterStyle = {};
                  let lineHeight = 100;
                  if (roundIndex === 1) {
                    lineHeight = 200;
                  }
                  if (roundIndex === 2) {
                    lineHeight = 350;
                  }
                  if (roundIndex === 3) {
                    lineHeight = 700;
                  }
                  if (roundIndex === 4) {
                    lineHeight = 1400;
                  }
                  const afterOffestEven = lineHeight - 50;
                  let afterOddStyle: MantineStyleProp = {
                    borderRightStyle: "solid",
                    borderTopStyle: "solid",
                    height: `${lineHeight}%`,
                    top: "50%",
                    borderRadius: "0 20px 0 0",
                  };
                  // const afterTop = lineHeight / 1.5;
                  const afterEvenStyle: MantineStyleProp = {
                    borderRightStyle: "solid",
                    borderBottomStyle: "solid",
                    height: `${lineHeight}%`,
                    top: `-${afterOffestEven}%`,
                    borderRadius: "0 0 20px 0",
                  };
                  if (playoffRound)
                    afterOddStyle = {
                      borderTopStyle: "solid",
                      top: "50%",
                    };
                  return (
                    <Box
                      key={i}
                      component="li"
                      className="team-item"
                      p={0}
                      style={{
                        padding: ".5rem",
                        display: "block",
                        margin: ".5rem 10px",
                        position: "relative",
                        verticalAlign: "middle",
                        lineHeight: 2,
                        textAlign: "center",
                        minWidth: eliminationRound.seeded ? 250 : 200,
                        "&:after": afterStyle,
                        "&:nth-of-type(odd):after": afterOddStyle,
                        "&:nth-of-type(even):after": afterEvenStyle,

                        "&:before": beforeStyle,
                      }}
                    >
                      <Card p={10} ta="center" mih={58}>
                        <MatchupDisplay
                          elimination={eliminationRound.elimination}
                          matchup={matchup}
                          event={event}
                          openMatch={openMatch}
                          matchupIndex={i}
                          disabled={!enabled}
                        />
                      </Card>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
export default EliminationBracket;
